import React, { useState, useEffect } from 'react';
import { ProgramModel } from '../../types/program';
import Modal from '../../Common/Components/Modal';
import { ProgramsService } from '../../services/programsService';
import { Upload, X } from 'lucide-react';
import { toast } from 'react-toastify';

interface EditProgramDialogProps {
  program: ProgramModel;
  onClose: () => void;
  onSave: (program: ProgramModel) => Promise<void>;
  show: boolean;
}

const EditProgramDialog: React.FC<EditProgramDialogProps> = ({
  program,
  onClose,
  onSave,
  show
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [url, setUrl] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (program) {
      setTitle(program.title);
      setDescription(program.description);
      setUrl(program.url);
      setSelectedFile(null);
    }
  }, [program]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      setSelectedFile(file);
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
  };

  const handleSave = async () => {
    if (!title.trim() || !description.trim() || !url.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsLoading(true);
    try {
      let photoUrl = program.photoUrl;

      if (selectedFile) {
        photoUrl = await ProgramsService.uploadProgramImage(selectedFile);
      }

      const updatedProgram: ProgramModel = {
        ...program,
        title: title.trim(),
        description: description.trim(),
        photoUrl,
        url: url.trim(),
      };

      await onSave(updatedProgram);
      onClose();
    } catch (error) {
      console.error('Error updating program:', error);
      toast.error('Failed to update program. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <Modal show={show} onHide={handleClose} id="editProgramModal" modal-center="true"
      className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
      dialogClassName="w-[calc(100vw-2rem)] mx-4 md:mx-0 md:w-[30rem] bg-white shadow rounded-md dark:bg-zink-600 flex flex-col h-full">
      <Modal.Header className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-zink-500"
        closeButtonClass="transition-all duration-200 ease-linear text-slate-500 hover:text-red-500 dark:text-zink-200 dark:hover:text-red-500">
        <Modal.Title className="text-16">Edit Program</Modal.Title>
      </Modal.Header>
      <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Program Image</label>
            <div className="relative border-2 border-dashed border-slate-300 dark:border-zink-500 rounded-lg p-4 cursor-pointer" onClick={() => document.getElementById('edit-program-image-input')?.click()}>
              {selectedFile ? (
                <div className="flex items-center gap-3">
                  <div className="size-12 bg-slate-100 rounded-lg dark:bg-zink-600 flex items-center justify-center">
                    <Upload className="size-6 text-slate-500 dark:text-zink-200" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{selectedFile.name}</p>
                    <p className="text-xs text-slate-500 dark:text-zink-400">
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFile();
                    }}
                    className="flex items-center justify-center size-8 rounded-md bg-red-100 text-red-500 hover:bg-red-200 dark:bg-red-500/20 dark:hover:bg-red-500/30"
                  >
                    <X className="size-4" />
                  </button>
                </div>
              ) : (
                <div className="text-center">
                  <div className="size-16 bg-slate-100 rounded-full dark:bg-zink-600 mx-auto mb-2 flex items-center justify-center">
                    <img
                      src={program.photoUrl}
                      alt=""
                      className="h-16 w-16 rounded-full object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                        if (nextElement) nextElement.style.display = 'flex';
                      }}
                    />
                    <div className="size-16 bg-gray-100 rounded-full dark:bg-gray-500/20 items-center justify-center hidden">
                      <span className="text-gray-500 text-2xl">📚</span>
                    </div>
                  </div>
                  <p className="text-sm text-slate-600 dark:text-zink-300 mb-2">
                    Click to change program image
                  </p>
                  <p className="text-xs text-slate-500 dark:text-zink-400">
                    PNG, JPG up to 5MB
                  </p>
                </div>
              )}
              <input
                id="edit-program-image-input"
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Program Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:border-custom-500 dark:bg-zink-700 dark:border-zink-500"
              placeholder="Enter program title"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Program Description *</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:border-custom-500 dark:bg-zink-700 dark:border-zink-500 resize-none"
              placeholder="Enter program description"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Program URL *</label>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:border-custom-500 dark:bg-zink-700 dark:border-zink-500"
              placeholder="https://example.com/program"
            />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="flex items-center justify-between p-4 mt-auto border-t border-slate-200 dark:border-zink-500">
        <button
          onClick={handleClose}
          className="px-4 py-2 text-sm bg-slate-100 hover:bg-slate-200 dark:bg-zink-600 dark:hover:bg-zink-500 rounded-md transition-colors"
          disabled={isLoading}
        >
          Close
        </button>
        <button
          onClick={handleSave}
          disabled={isLoading}
          className="px-4 py-2 text-sm bg-custom-500 text-white hover:bg-custom-600 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {isLoading && (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          )}
          Update
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditProgramDialog;